import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "./lib/api";
import { meQueryKey } from "./lib/auth";
import { routeTree } from "./routeTree.gen";
import "./index.css";

/**
 * Any 401 means the session is gone (sessions expire Sunday midnight). Forget
 * the cached user and send the person to the login page, remembering where
 * they were. The /auth/me/ query maps 401 to null itself and never lands here.
 */
function handleUnauthenticated(error: unknown) {
  if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
  if (router.state.location.pathname === "/login") return;
  queryClient.setQueryData(meQueryKey, null);
  toast.info("Your session has expired. Please sign in again.");
  void router.navigate({
    to: "/login",
    search: { redirect: router.state.location.href },
    replace: true,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
  queryCache: new QueryCache({ onError: handleUnauthenticated }),
  mutationCache: new MutationCache({ onError: handleUnauthenticated }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
